import { faLeftLong, faRightLong } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { combine } from '../../../../helpers/styles';
import { SESSION_TYPES } from '../../../../models/session';
import Checkbox from '../../../checkbox';
import { NUM_OF_EVALUATIONS_WHEN_DECIDED } from '../stats';
import BarPlot from './barplot';
import styles from './styles.module.scss';

const NUM_OF_EVALUATIONS = {
  [SESSION_TYPES.PRESELECTION]: {
    partiallyDecidedSubmissions: [1, 2],
    fullyDecidedSubmissions: [3],
  },
  [SESSION_TYPES.FINAL_JURY]: [1],
};

const INITIAL_FILTERS = {
  [SESSION_TYPES.PRESELECTION]: {
    submissionType: {
      regularSubmissions: true,
    },
    decisionStatus: {
      fullyDecidedSubmissions: true,
      partiallyDecidedSubmissions: false,
    },
  },
  [SESSION_TYPES.FINAL_JURY]: {
    submissionType: {
      regularSubmissions: true,
      cooperations: true,
      wildcards: true,
    },
    decisionStatus: {
      fullyDecidedSubmissions: true,
    },
  },
};

const getFilter = (submissions, sessionType) => {
  const filter = JSON.parse(JSON.stringify(INITIAL_FILTERS[sessionType]));

  if (sessionType === SESSION_TYPES.PRESELECTION) {
    // Should not happen with real data, but for testing data it is possible
    if (submissions.some((submission) => submission?.isCooperation)) {
      filter.submissionType.cooperations = true;
    }
    if (submissions.some((submission) => submission?.isWildcard)) {
      filter.submissionType.wildcards = true;
    }
  }

  return filter;
};

const generateLabels = (maxPoints, pointStep, decidedEvaluationOptions) => {
  let labels = new Set();

  decidedEvaluationOptions.forEach((decidedEvaluationOption) => {
    const currentPointStep = pointStep / decidedEvaluationOption;

    for (let j = 0; j <= maxPoints; j += currentPointStep) {
      let label = j.toFixed(0);
      labels.add(label);
    }
  });

  labels = [...labels];

  labels.sort((a, b) => Number(a) - Number(b));

  return labels;
};

const Chart = ({
  submissions,
  session,
  preselectionThreshold,
  initialThreshold,
}) => {
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState(initialThreshold ?? 0);
  const [filter, setFilter] = useState(
    getFilter(submissions, session?.sessionType)
  );

  useEffect(() => {
    setFilter(getFilter(submissions, session?.sessionType));
  }, [session, submissions]);

  const data = useMemo(() => {
    if (!session || !submissions?.length) return null;

    const maxPointsForSession =
      session.maxPointsPerCriterion * session.criteria.length;

    let decidedEvaluationOptions = [];

    if (session.sessionType === SESSION_TYPES.FINAL_JURY) {
      decidedEvaluationOptions.push(
        ...NUM_OF_EVALUATIONS[SESSION_TYPES.FINAL_JURY]
      );
    }

    if (session.sessionType === SESSION_TYPES.PRESELECTION) {
      if (filter.decisionStatus.fullyDecidedSubmissions) {
        decidedEvaluationOptions.push(
          ...NUM_OF_EVALUATIONS[SESSION_TYPES.PRESELECTION]
            .fullyDecidedSubmissions
        );
      }

      if (filter.decisionStatus.partiallyDecidedSubmissions) {
        decidedEvaluationOptions.push(
          ...NUM_OF_EVALUATIONS[SESSION_TYPES.PRESELECTION]
            .partiallyDecidedSubmissions
        );
      }
    }

    const labels = generateLabels(
      maxPointsForSession,
      session.pointsStep,
      decidedEvaluationOptions.sort((a, b) => a - b)
    );

    const counts = new Array(labels.length).fill(0);

    const filteredSubmissions = submissions.filter((submission) => {
      let submissionTypeIsIncluded = false;
      let decisionStatusIsIncluded = false;

      // Skip submissions that have not been decided yet
      if (submission?.numDecidedEvaluations === 0) return false;

      if (filter.submissionType.regularSubmissions) {
        submissionTypeIsIncluded =
          submissionTypeIsIncluded ||
          (!submission?.isCooperation && !submission.isWildcard);
      }
      if (filter.submissionType.cooperations) {
        submissionTypeIsIncluded =
          submissionTypeIsIncluded || submission?.isCooperation;
      }
      if (filter.submissionType.wildcards) {
        submissionTypeIsIncluded =
          submissionTypeIsIncluded || submission?.isWildcard;
      }

      // Skip early if submission type is not included
      if (!submissionTypeIsIncluded) return false;

      if (filter.decisionStatus.partiallyDecidedSubmissions) {
        decisionStatusIsIncluded =
          decisionStatusIsIncluded ||
          (submission?.numDecidedEvaluations > 0 &&
            submission?.numDecidedEvaluations <
              NUM_OF_EVALUATIONS_WHEN_DECIDED[session?.sessionType]);
      }

      if (filter.decisionStatus.fullyDecidedSubmissions) {
        decisionStatusIsIncluded =
          decisionStatusIsIncluded ||
          submission?.numDecidedEvaluations >=
            NUM_OF_EVALUATIONS_WHEN_DECIDED[session?.sessionType];
      }

      return submissionTypeIsIncluded && decisionStatusIsIncluded;
    });

    filteredSubmissions.forEach((submission) => {
      const key = submission.finalPoints;
      if (isNaN(key)) console.log('key', key);
      if(key < 0) console.log('key', key);
      const roundedKey = key.toFixed(0);
      let keyIndex = labels.indexOf(roundedKey);

      // If exact match not found, find the closest label
      if (keyIndex < 0) {
        const numericLabels = labels.map(Number);
        const numericKey = Number(roundedKey);
        
        // Find the closest label by absolute difference
        let minDiff = Infinity;
        let closestIndex = -1;
        
        numericLabels.forEach((label, index) => {
          const diff = Math.abs(label - numericKey);
          if (diff < minDiff) {
            minDiff = diff;
            closestIndex = index;
          }
        });
        
        keyIndex = closestIndex;
      }

      if (keyIndex >= 0 && keyIndex < counts.length) {
        counts[keyIndex] += 1;
      }
    });

    return { labels, data: counts };
  }, [submissions, session, filter]);

  // Ensure threshold is valid when data changes
  useEffect(() => {
    if (data && data.labels && data.labels.length > 0) {
      // Convert threshold to string for comparison since labels are strings
      const thresholdStr = String(threshold);
      const thresholdExists = data.labels.includes(thresholdStr);
      if (!thresholdExists) {
        // Set threshold to the first label if current threshold doesn't exist
        setThreshold(data.labels[0]);
      }
    }
  }, [data]); // Only depend on data, not threshold to avoid infinite loops

  const stats = useMemo(() => {
    if (!data) return null;

    const thresholdLabelIndex = data.labels.findIndex(
      (label) => label === threshold
    );

    const entryCount = data.data.reduce((acc, curr) => acc + curr, 0);
    const notAwardedEntries = data.data
      .slice(0, thresholdLabelIndex)
      .reduce((acc, curr) => acc + curr, 0);
    const awardedEntries = data.data
      .slice(thresholdLabelIndex)
      .reduce((acc, curr) => acc + curr, 0);
    return {
      notAwardedEntries: {
        absolute: notAwardedEntries,
        percentage: (notAwardedEntries / entryCount) * 100,
      },
      awardedEntries: {
        absolute: awardedEntries,
        percentage: (awardedEntries / entryCount) * 100,
      },
    };
  }, [threshold, data]);

  const handleThresholdIncrement = () => {
    if (!data || !data.labels || data.labels.length === 0) return;
    const idx = data.labels.findIndex((label) => label === threshold);
    if (idx < 0) {
      // If threshold not found, set to first label
      setThreshold(data.labels[0]);
      return;
    }
    const nextIdx = idx + 1;
    if (nextIdx >= data.labels.length) return;
    setThreshold(data.labels[nextIdx]);
  };

  const handleThresholdDecrement = () => {
    if (!data || !data.labels || data.labels.length === 0) return;
    const idx = data.labels.findIndex((label) => label === threshold);
    if (idx < 0) {
      // If threshold not found, set to first label
      setThreshold(data.labels[0]);
      return;
    }
    const prevIdx = idx - 1;
    if (prevIdx < 0) return;
    setThreshold(data.labels[prevIdx]);
  };

  if (!data || !stats)
    return <div>{t('scoreMonitor.chartView.chart.processingData')}</div>;

  return (
    <Container className={styles.chartContainer}>
      <Row className={combine('mt-5', 'mb-5', styles.filterOptions)}>
        <Col className={styles.filterOptionsOuterContainer}>
          <div className={styles.filterOptionsInnerContainer}>
            <span className={styles.title}>
              {t('scoreMonitor.chartView.chart.filter.title')}
            </span>
            <div className={styles.content}>
              <span className={styles.subtitle}>
                {t('scoreMonitor.chartView.chart.filter.submissionType.title')}
              </span>
              {Object.entries(filter.submissionType).map(([key, value]) => (
                <Checkbox
                  key={key}
                  label={t(
                    `scoreMonitor.chartView.chart.filter.submissionType.${key}`
                  )}
                  name={key}
                  value={value}
                  onChange={() => {
                    setFilter((prev) => ({
                      ...prev,
                      submissionType: {
                        ...prev.submissionType,
                        [key]: !prev.submissionType[key],
                      },
                    }));
                  }}
                  readOnly={
                    Object.values(filter.submissionType).filter(Boolean)
                      .length === 1 && value
                  }
                />
              ))}
            </div>
            <div className={styles.content}>
              <span className={styles.subtitle}>
                {t('scoreMonitor.chartView.chart.filter.decisionStatus.title')}
              </span>
              {Object.entries(filter.decisionStatus).map(([key, value]) => (
                <Checkbox
                  key={key}
                  label={t(
                    `scoreMonitor.chartView.chart.filter.decisionStatus.${key}`
                  )}
                  name={key}
                  value={value}
                  onChange={() => {
                    setFilter((prev) => ({
                      ...prev,
                      decisionStatus: {
                        ...prev.decisionStatus,
                        [key]: !prev.decisionStatus[key],
                      },
                    }));
                  }}
                  readOnly={
                    Object.values(filter.decisionStatus).filter(Boolean)
                      .length === 1 && value
                  }
                />
              ))}
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <BarPlot
          key={JSON.stringify(filter)}
          data={data}
          threshold={threshold}
          setThreshold={setThreshold}
          sessionType={session?.sessionType}
          preselectionThreshold={preselectionThreshold}
          awardedEntries={stats.awardedEntries}
          notAwardedEntries={stats.notAwardedEntries}
        />
      </Row>
      {data && data.labels && data.labels.length > 0 && (
        <Row>
          <Col className={styles.thresholdContainer}>
            <div className={styles.arrowIconContainer}>
              <FontAwesomeIcon
                icon={faLeftLong}
                className={styles.arrowIcon}
                onClick={handleThresholdDecrement}
              />
            </div>
            <div className={styles.thresholdValueContainer}>
              <span className={styles.thresholdValue}>
                <Trans
                  i18nKey="scoreMonitor.chartView.chart.threshold"
                  components={{
                    red: <span className={styles.thresholdValueNumber} />,
                  }}
                  values={{ threshold: threshold }}
                />
              </span>
            </div>
            <div className={styles.arrowIconContainer}>
              <FontAwesomeIcon
                icon={faRightLong}
                className={styles.arrowIcon}
                onClick={handleThresholdIncrement}
              />
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Chart;
